// ./src/threadPaging.ts
//
// ROADMAP #128 — a message thread (the crew chat, a guest's direct messages) is read a page at a time: the newest page
// on open, then "Load older" reads the page before the oldest message on screen. These are the merges, kept pure so a
// guard RUNS them (apps/app `check-thread-paging`). Both used to show a thread's newest 50 and nothing older.
//
// ⚠ "Load older" reads by a CURSOR — the oldest message shown, its id with its createdAt (`cursorOf`) — never page 2:
// a message sent while the thread is open shifts every offset. #133: the server reads strictly older by (created_at, id)
// against that message's STORED time, so messages sharing one millisecond (a bulk insert) page one after another. It
// used to be a time bound one millisecond past the message, which brought repeats and, past a page of them, stalled.
// The merges still drop a repeat by id (a message can reach the screen live AND in a page) — never a gap.

/** How many messages one read of a thread brings. */
export const THREAD_PAGE_SIZE = 50;

/** What the merges need of a message. createdAt is ISO, so text order is time order. */
export interface ThreadMessage {
  id: string;
  createdAt?: string | null;
}

/**
 * #133 — what a "Load older / Load more" read sends: the oldest message shown (`beforeId`) and its createdAt (`before`,
 * which the server uses only if that message no longer exists). The services send it in their OWN query document.
 */
export interface ThreadCursor {
  beforeId: string;
  before: string | null;
}

/** The cursor that reads the messages older than `message`, or null when there is no message. */
export const cursorOf = <T extends ThreadMessage>(message: T | null | undefined): ThreadCursor | null =>
  message?.id ? { beforeId: message.id, before: message.createdAt ?? null } : null;

/**
 * The page before the oldest message on screen (`rows` newest-first, as the server sends them) merged in above it;
 * `onScreen` is oldest→newest. More may be older when the page came back FULL — and brought something new: a full
 * page of nothing but repeats would offer the same read for ever (it cannot happen under the #133 cursor; kept so a
 * server that ever answers that way ends the paging instead of looping).
 */
export const mergeOlderPage = <T extends ThreadMessage>(onScreen: T[], rows: T[]): { messages: T[]; hasOlder: boolean } => {
  const shown = new Set(onScreen.map((m) => m.id));
  const older = rows.filter((m) => !shown.has(m.id)).reverse();

  return {
    messages: older.length > 0 ? [...older, ...onScreen] : onScreen,
    hasOlder: rows.length >= THREAD_PAGE_SIZE && older.length > 0,
  };
};

/** Oldest→newest by createdAt; equal times keep their order (the sort is stable). */
const inTimeOrder = <T extends ThreadMessage>(list: T[]): T[] =>
  list.slice().sort((a, b) => (String(a.createdAt ?? "") < String(b.createdAt ?? "") ? -1 : String(a.createdAt ?? "") > String(b.createdAt ?? "") ? 1 : 0));

/**
 * A re-read of the newest page (after a live stream reconnected; `page` oldest→newest) against what is on screen NOW.
 * `knownAtStart` = the ids that were on screen when the read went out. `hasOlder: null` = leave it as it was.
 *  • a message on screen now that was NOT there when the read went out, and is not in the page, ARRIVED while it was in
 *    flight (a live frame, our own send, a Load older) — the page was served without it, so it stays, in time order;
 *  • a page that is not full IS the whole thread — it replaces everything else, nothing older;
 *  • a full page that shares a message with the screen joins it with no gap — the older messages "Load older" brought
 *    stay, everything inside the page's span is the page's (a removed message goes). "Older" is decided by POSITION on
 *    screen (before the first message the page shares) as well as by time: #133 — messages sharing the page's oldest
 *    millisecond read as EQUAL times, and a time test alone dropped them from the middle of the thread;
 *  • a full page that shares NOTHING may have a gap behind it (more than a page arrived while disconnected) — keeping
 *    the older ones would hide that gap for good, so the page replaces them and "Load older" reads down from it.
 */
export const mergeNewestPage = <T extends ThreadMessage>(
  onScreen: T[],
  page: T[],
  full: boolean,
  knownAtStart: ReadonlySet<string>
): { messages: T[]; hasOlder: boolean | null } => {
  const pageIds = new Set(page.map((m) => m.id));
  const arrived = onScreen.filter((m) => !knownAtStart.has(m.id) && !pageIds.has(m.id));

  if (!full) {
    return { messages: inTimeOrder([...page, ...arrived]), hasOlder: false };
  }

  if (!onScreen.some((m) => pageIds.has(m.id))) {
    return { messages: inTimeOrder([...page, ...arrived]), hasOlder: true };
  }

  const oldestInPage = String(page[0]?.createdAt ?? "");
  const firstShared = onScreen.findIndex((m) => pageIds.has(m.id));
  const older = onScreen.filter((m, i) => i < firstShared && knownAtStart.has(m.id) && !pageIds.has(m.id) && String(m.createdAt ?? "") <= oldestInPage);

  return { messages: inTimeOrder([...older, ...page, ...arrived]), hasOlder: null };
};

/**
 * ROADMAP #128 — the "Load more" of a NEWEST-FIRST feed (the guest book, a comment's replies, the comments page): the
 * page older than the oldest item shown (`rows`, newest-first, read by its cursor — `olderCursor`) appended BELOW.
 * A repeat is dropped by id. More may be older when the page came back FULL and brought something new — a full page of
 * repeats would offer the same read for ever.
 */
export const appendOlderPage = <T extends ThreadMessage>(shown: T[], rows: T[], pageSize: number): { items: T[]; hasMore: boolean } => {
  const have = new Set(shown.map((m) => m.id));
  const older = rows.filter((m) => !have.has(m.id));

  return {
    items: older.length > 0 ? [...shown, ...older] : shown,
    hasMore: rows.length >= pageSize && older.length > 0,
  };
};

/** The cursor a newest-first feed's Load more reads before: its oldest (last) item, or null when it shows none. */
export const olderCursor = <T extends ThreadMessage>(shown: T[]): ThreadCursor | null => cursorOf(shown[shown.length - 1]);
